import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import api, { setAccessToken } from '../api/client';

// User type defined locally
interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  role: 'USER' | 'ADMIN' | 'MODERATOR';
  provider?: 'LOCAL' | 'GOOGLE' | 'FACEBOOK' | 'APPLE';
}

interface Tokens {
  accessToken: string;
  refreshToken: string;
}

interface AuthState {
  user: User | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  rememberMe: boolean;

  // Actions
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  loginWithFacebook: (accessToken: string) => Promise<void>;
  loginWithApple: (idToken: string, userData?: { firstName?: string; lastName?: string }) => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

// Helper to handle successful auth response
const handleAuthSuccess = (
  set: (partial: Partial<AuthState>) => void,
  response: { data?: { user: User; tokens: Tokens } },
  rememberMe: boolean = true
) => {
  if (response.data) {
    setAccessToken(response.data.tokens.accessToken);
    set({
      user: response.data.user,
      refreshToken: response.data.tokens.refreshToken,
      isAuthenticated: true,
      isLoading: false,
      rememberMe,
    });
  }
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      rememberMe: false,

      login: async (email: string, password: string, rememberMe = false) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{ user: User; tokens: Tokens }>(
            '/auth/login',
            { email, password }
          );
          handleAuthSuccess(set, response, rememberMe);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      loginWithGoogle: async (idToken: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{ user: User; tokens: Tokens }>(
            '/auth/google',
            { idToken }
          );
          handleAuthSuccess(set, response, true);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Google login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      loginWithFacebook: async (accessToken: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{ user: User; tokens: Tokens }>(
            '/auth/facebook',
            { accessToken }
          );
          handleAuthSuccess(set, response, true);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Facebook login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      loginWithApple: async (idToken: string, userData?: { firstName?: string; lastName?: string }) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{ user: User; tokens: Tokens }>(
            '/auth/apple',
            { idToken, user: userData }
          );
          handleAuthSuccess(set, response, true);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Apple login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      register: async (email: string, password: string, firstName?: string, lastName?: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<{ user: User; tokens: Tokens }>(
            '/auth/register',
            { email, password, firstName, lastName }
          );
          handleAuthSuccess(set, response, true);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Registration failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch {
          // Ignore logout errors
        } finally {
          setAccessToken(null);
          set({
            user: null,
            refreshToken: null,
            isAuthenticated: false,
            error: null,
            rememberMe: false,
          });
        }
      },

      checkAuth: async () => {
        const { refreshToken } = get();

        // If no refresh token, don't try to restore session
        if (!refreshToken) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        set({ isLoading: true });
        try {
          // Use the stored refresh token to get a new access token
          const refreshResponse = await api.post<{ accessToken: string; refreshToken: string }>(
            '/auth/refresh',
            { refreshToken }
          );
          if (refreshResponse.data?.accessToken) {
            setAccessToken(refreshResponse.data.accessToken);
            // Store the new refresh token (token rotation)
            if (refreshResponse.data.refreshToken) {
              set({ refreshToken: refreshResponse.data.refreshToken });
            }
            // Now get user data with the fresh token
            const userResponse = await api.get<User>('/auth/me');
            if (userResponse.data) {
              set({
                user: userResponse.data,
                isAuthenticated: true,
                isLoading: false,
              });
              return;
            }
          }
          throw new Error('Failed to refresh auth');
        } catch {
          setAccessToken(null);
          set({
            user: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => {
        // Only persist if rememberMe is true or if there's an active session
        if (state.rememberMe || state.isAuthenticated) {
          return {
            user: state.user,
            refreshToken: state.refreshToken,
            isAuthenticated: state.isAuthenticated,
            rememberMe: state.rememberMe,
          };
        }
        return {};
      },
    }
  )
);
